import React, { useCallback, useMemo } from 'react';
import {
  UnlockDorsLinkContainer,
  UnlockDorsItemContainer,
  UnlockDorsInfo,
  UnlockDorsTitle,
  UnlockDorsDescription,
  UnlockDorsKeyIconContainer,
  UnlockDorsChevronIconContainer,
} from './styles';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { BOOKING_STATUSES, ACTION_STATUSES, PATCH_STATUSES } from 'shared/consts';
import { TransactionDetails } from 'store/transactions/types';
import { selectCart } from 'store/cart/selectors';
import { searchParams } from 'shared/types/search-params';
import { isBookingsForOneResource } from 'components/payment/receipt/components/utils/isBookingsForOneResource';
import { Key_01, ChevronRight } from '@hqo/react-components-library/dist/icons';
import { useGetTheme } from 'hooks/use-get-theme.hook';
import { selectPatchTransactionsStatus, selectTypeOfPatchTransaction } from 'store/transactions/selectors';
import { useMiniappSdk } from 'components/miniapp-sdk-provider/miniapp-sdk.hook';
import { useIsMobile } from 'hooks/use-is-mobile.hook';

interface UnlockDorsLinkProps {
  transaction: TransactionDetails;
  isSwipeModalContent?: boolean;
}

export const UnlockDorsLink = ({ transaction, isSwipeModalContent = false }: UnlockDorsLinkProps) => {
  const openPathCloudKeys = transaction.details?.openpath_cloud_keys || [];
  const theme = useGetTheme();
  const isMobile = useIsMobile();
  const { formatMessage } = useIntl();
  const client = useMiniappSdk();

  const cart = useSelector(selectCart);
  const patchTransactionStatus = useSelector(selectPatchTransactionsStatus);
  const typeOfPatchTransaction = useSelector(selectTypeOfPatchTransaction);
  const patchTransactionData = useMemo(
    () => ({
      status: patchTransactionStatus,
      type: typeOfPatchTransaction,
    }),
    [patchTransactionStatus, typeOfPatchTransaction],
  );

  const handleLinkRedirect = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      client.header.showHeader();
      event.stopPropagation();
    },
    [client],
  );

  const { startDates } = useSearchParams<searchParams>();
  const multiDayBookingInfo = useMemo<{ resourceName: string; status: string } | null>(() => {
    const isMultiDayBooking = isBookingsForOneResource(cart?.items);

    return !!startDates?.length && isMultiDayBooking
      ? { resourceName: cart?.items[0].resource_booking.name, status: cart.items[0].status }
      : null;
  }, [cart?.items, startDates?.length]);

  const booking_status = multiDayBookingInfo ? multiDayBookingInfo.status : transaction?.details.status;
  const bookingCancelled =
    patchTransactionData.status === ACTION_STATUSES.FULFILLED && patchTransactionData.type === PATCH_STATUSES.CANCELLED;

  if (booking_status !== BOOKING_STATUSES.ACCEPTED || openPathCloudKeys.length === 0 || bookingCancelled) {
    return null;
  }

  return (
    <UnlockDorsLinkContainer $isSwipeModalContent={isSwipeModalContent} data-testid="unlock-doors-container">
      {openPathCloudKeys?.map(openPathCloudKey => (
        <UnlockDorsItemContainer
          key={openPathCloudKey.uuid}
          href={openPathCloudKey.url}
          target={isMobile ? '_self' : '_blank'}
          onClick={handleLinkRedirect}
          data-testid="unlock-doors-link-container"
        >
          <UnlockDorsKeyIconContainer>
            <Key_01 width={20} height={20} color={theme.colors.$primary} />
          </UnlockDorsKeyIconContainer>
          <UnlockDorsInfo>
            <UnlockDorsTitle>{formatMessage({ id: 'unlock_doors_title' })}</UnlockDorsTitle>
            <UnlockDorsDescription>
              {formatMessage({ id: 'unlock_doors_description' }, { linkLabel: openPathCloudKey.label })}
            </UnlockDorsDescription>
          </UnlockDorsInfo>
          <UnlockDorsChevronIconContainer>
            <ChevronRight width={16} height={16} color={theme.colors.$primary} />
          </UnlockDorsChevronIconContainer>
        </UnlockDorsItemContainer>
      ))}
    </UnlockDorsLinkContainer>
  );
};
